import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FilterCard from './FilterCard';
import SearchField from './SearchField';
import { useSelector } from 'react-redux';
import {
  accessFilterItemsSelector,
  categoriesFilterItemsSelector,
  companiesFilterItemsSelector,
} from '@/store/searchFilters/selectors';
import { Language } from '@/types';
import { getSpacing } from '@/styles/theme';

interface FilterState {
  search: string;
  companies: string[];
  categories: string[];
  apiAccess: string[];
}

interface ApiFilterSidebarProps {
  onFiltersChange: (filters: FilterState) => void;
}

const ApiFilterSidebar: React.FC<ApiFilterSidebarProps> = ({ onFiltersChange }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const selectedLanguage = useMemo(() => i18n.language as Language, [i18n.language]);

  const companiesFilterItems = useSelector(companiesFilterItemsSelector);
  const categoriesFilterItems = useSelector(categoriesFilterItemsSelector(selectedLanguage));
  const accessFilterItems = useSelector(accessFilterItemsSelector(t));

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    companies: [],
    categories: [],
    apiAccess: [],
  });

  const updateFilters = useCallback(
    (updates: Partial<FilterState>) => {
      const newFilters = { ...filters, ...updates };
      setFilters(newFilters);
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateFilters({ search: event.target.value });
    },
    [updateFilters],
  );

  const handleClearSearch = useCallback(() => {
    updateFilters({ search: '' });
  }, [updateFilters]);

  const handleCompanyChange = useCallback(
    (company: string, checked: boolean) => {
      const newCompanies = checked
        ? [...filters.companies, company]
        : filters.companies.filter((c) => c !== company);
      updateFilters({ companies: newCompanies });
    },
    [filters.companies, updateFilters],
  );

  const handleCategoryChange = useCallback(
    (category: string, checked: boolean) => {
      const newCategories = checked
        ? [...filters.categories, category]
        : filters.categories.filter((c) => c !== category);
      updateFilters({ categories: newCategories });
    },
    [filters.categories, updateFilters],
  );

  const handleLabelChange = useCallback(
    (label: string, checked: boolean) => {
      const newApiAccess = checked
        ? [...filters.apiAccess, label]
        : filters.apiAccess.filter((l) => l !== label);
      updateFilters({ apiAccess: newApiAccess });
    },
    [filters.apiAccess, updateFilters],
  );

  const resetFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      companies: [],
      categories: [],
      apiAccess: [],
    };
    setFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  }, [onFiltersChange]);

  const clearAllFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  useEffect(() => {
    resetFilters();
  }, [selectedLanguage]);

  const hasActiveFilters =
    filters.companies.length > 0 || filters.categories.length > 0 || filters.apiAccess.length > 0;

  return (
    <Box
      sx={{
        borderRadius: theme.customBorderRadius.md,
        margin: '32px',
        width: 320,
        backgroundColor: theme.palette.ui.background,
        borderRight: `1px solid ${theme.palette.ui.border}`,
      }}
    >
      {/* Filter Container */}
      <Box sx={{ p: getSpacing.xs, padding: 0, paddingTop: 2 }}>
        <Typography
          variant='h5'
          gutterBottom
          sx={{
            textAlign: 'center',
            color: theme.palette.brand.primary,
          }}
        >
          {t('apiFilter.title')}
        </Typography>

        <SearchField
          searchTerm={filters.search}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />

        {/* Filter Cards */}
        {companiesFilterItems.length > 0 && (
          <FilterCard
            title={t('apiFilter.company')}
            items={companiesFilterItems}
            selectedItems={filters.companies}
            onItemChange={handleCompanyChange}
          />
        )}

        {categoriesFilterItems.length > 0 && (
          <FilterCard
            title={t('apiFilter.category')}
            items={categoriesFilterItems}
            selectedItems={filters.categories}
            onItemChange={handleCategoryChange}
          />
        )}

        {accessFilterItems.length > 0 && (
          <FilterCard
            title={t('apiFilter.apiAccess')}
            items={accessFilterItems}
            selectedItems={filters.apiAccess}
            onItemChange={handleLabelChange}
          />
        )}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              margin: '10px',
              marginRight: '30px',
            }}
          >
            <Button
              onClick={clearAllFilters}
              sx={{
                textTransform: 'none',
                color: theme.palette.brand.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action?.hover,
                },
              }}
            >
              <ClearIcon fontSize='small' />
              {t('apiFilter.deleteFilters')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ApiFilterSidebar;
